import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  ConfirmationNumber as TicketIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailable as EventIcon,
  Euro as EuroIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { dashboardAPI } from '../services/api';

const StatCard = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: color + '20',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { color: color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const getStatusColor = (status) => {
  const colors = {
    confirmed: 'success',
    pending: 'warning',
    cancelled: 'error',
    completed: 'info',
  };
  return colors[status] || 'default';
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.stats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      setError('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 900 }}>
        Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Gesamt Buchungen"
            value={stats?.totalBookings || 0}
            icon={<TicketIcon />}
            color="#b83e31"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Heute"
            value={stats?.todayBookings || 0}
            icon={<TrendingUpIcon />}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Bevorstehend"
            value={stats?.upcomingBookings || 0}
            icon={<EventIcon />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monat Umsatz"
            value={`CHF ${stats?.monthRevenue?.toFixed(2) || '0.00'}`}
            icon={<EuroIcon />}
            color="#ff9800"
          />
        </Grid>
      </Grid>

      {/* Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                Buchungen (Letzte 7 Tage)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats?.bookingsPerDay || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#b83e31" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                Status Übersicht
              </Typography>
              {stats?.bookingsByStatus?.map((item) => (
                <Box key={item.status} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={item.status} color={getStatusColor(item.status)} size="small" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {item.count}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                Bevorstehende Events
              </Typography>
              {stats?.upcomingEvents && stats.upcomingEvents.length > 0 ? (
                <Box>
                  {stats.upcomingEvents.map((event, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'action.hover',
                        '&:hover': { backgroundColor: 'action.selected' }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {event.date}
                        </Typography>
                        <Chip
                          label={`${event.count} Buchungen`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {event.time_slot} • {event.total_children} Kinder
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Keine bevorstehenden Events
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Bookings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                Letzte Buchungen
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Buchungs-ID</strong></TableCell>
                      <TableCell><strong>Kunde</strong></TableCell>
                      <TableCell><strong>Event</strong></TableCell>
                      <TableCell><strong>Datum</strong></TableCell>
                      <TableCell><strong>Zeit</strong></TableCell>
                      <TableCell><strong>Preis</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats?.recentBookings?.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell>{booking.booking_id}</TableCell>
                        <TableCell>{booking.first_name} {booking.last_name}</TableCell>
                        <TableCell>{booking.event_name}</TableCell>
                        <TableCell>{booking.booking_date}</TableCell>
                        <TableCell>{booking.time_slot}</TableCell>
                        <TableCell>CHF {parseFloat(booking.total_price).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip label={booking.status} color={getStatusColor(booking.status)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
